import { useEffect, useId, useState } from 'react';
import {
  columnAlign,
  computeTotals,
  documentLabel,
  expiryNote,
  formatCell,
  formatDate,
  formatDateTime,
  formatTile,
  isExpired,
  laneLabel,
  stageLabel,
  stageTone,
  type ShareColumn,
  type ShareDataset,
  type ShareDocument,
  type ShareRow,
  type ShareStatusHeader,
  type ShareSubTable,
} from './shareContract';

/**
 * The header of a live-status share: the stage, when it was last refreshed, and the facts a
 * customer asks first — which vehicle, which lane, where it was last seen. Everything here is
 * the sender's snapshot; the page never polls for movement.
 */
function StatusHeader({ status }: { status: ShareStatusHeader }) {
  const lane = laneLabel(status);
  const facts: { label: string; value: string }[] = [];
  if (status.vehicleNo) facts.push({ label: 'Vehicle', value: status.vehicleNo });
  if (lane) facts.push({ label: 'Route', value: lane });
  if (status.dispatchDate)
    facts.push({ label: 'Dispatched', value: formatDate(status.dispatchDate) });
  if (status.expectedDelivery) {
    facts.push({ label: 'Expected delivery', value: formatDate(status.expectedDelivery) });
  }
  if (status.lastLocation) {
    facts.push({
      label: 'Last seen',
      value: status.lastLocationAt
        ? `${status.lastLocation} · ${formatDateTime(status.lastLocationAt)}`
        : status.lastLocation,
    });
  }

  return (
    <section aria-label="Current status" className="share-status">
      <div className="share-status-head">
        <span className="share-status-pill" data-tone={stageTone(status.stage)}>
          {stageLabel(status)}
        </span>
        <span className="share-status-updated">
          Last updated {formatDateTime(status.updatedAt)}
        </span>
      </div>
      {facts.length > 0 && (
        <dl className="share-status-facts">
          {facts.map(fact => (
            <div className="share-status-fact" key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

function ChildTable({ table }: { table: ShareSubTable }) {
  return (
    <div className="share-child">
      <h3 className="share-child-name">{table.label}</h3>
      <table className="share-table share-table--child">
        <thead>
          <tr>
            {table.columns.map(column => (
              <th key={column.key} data-align={columnAlign(column)} scope="col">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map(row => (
            <tr key={row.id}>
              {table.columns.map(column => (
                <td key={column.key} data-align={columnAlign(column)} data-label={column.label}>
                  {formatCell(column, row.cells[column.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DataRow({
  columns,
  expandAll,
  row,
}: {
  columns: ShareColumn[];
  expandAll: boolean;
  row: ShareRow;
}) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const children = row.children ?? [];
  const expanded = expandAll || open;

  return (
    <>
      <tr className="share-row">
        {columns.map((column, index) => (
          <td key={column.key} data-align={columnAlign(column)} data-label={column.label}>
            {index === 0 && children.length > 0 && (
              <button
                aria-controls={detailsId}
                aria-expanded={expanded}
                className="share-expand"
                onClick={() => setOpen(!open)}
                type="button"
              >
                <span className="sr-only">
                  {expanded ? 'Hide details for' : 'Show details for'}{' '}
                  {String(row.cells[column.key] ?? 'this row')}
                </span>
              </button>
            )}
            {formatCell(column, row.cells[column.key])}
          </td>
        ))}
      </tr>
      {expanded && children.length > 0 && (
        // Collapsed children stay out of the DOM entirely, so nothing hidden lands in a print.
        <tr className="share-row-children" id={detailsId}>
          <td colSpan={columns.length}>
            {children.map(table => (
              <ChildTable key={table.id} table={table} />
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

function DatasetTable({ dataset, expandAll }: { dataset: ShareDataset; expandAll: boolean }) {
  const totals = computeTotals(dataset);

  return (
    <section className="share-dataset">
      <div className="share-dataset-head">
        <h2 className="share-dataset-name">{dataset.name}</h2>
        <span className="share-dataset-count">
          {dataset.rows.length} {dataset.rows.length === 1 ? 'row' : 'rows'}
        </span>
      </div>

      <div className="share-table-wrap">
        <table className="share-table">
          <thead>
            <tr>
              {dataset.columns.map(column => (
                <th key={column.key} data-align={columnAlign(column)} scope="col">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataset.rows.map(row => (
              <DataRow columns={dataset.columns} expandAll={expandAll} key={row.id} row={row} />
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="share-totals">
                {dataset.columns.map(column => (
                  <td key={column.key} data-align={columnAlign(column)} data-label="Total">
                    {totals[column.key] === undefined ? '' : formatCell(column, totals[column.key])}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

export function StatementView({ document: shared }: { document: ShareDocument }) {
  const { meta, payload } = shared;
  const [expandAll, setExpandAll] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Ctrl+P must produce the same page as the button does, sub-tables included.
    const before = () => setExpandAll(true);
    window.addEventListener('beforeprint', before);
    return () => window.removeEventListener('beforeprint', before);
  }, []);

  const print = () => {
    setExpandAll(true);
    requestAnimationFrame(() => window.print());
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const { letterhead } = payload;
  const expired = isExpired(meta.expiresAt);
  const isStatus = payload.kind === 'TRIP_STATUS';

  return (
    <article className="share-doc" data-test-org={payload.test ? 'true' : undefined}>
      {payload.test && (
        <div aria-hidden="true" className="share-test-watermark">
          TEST
        </div>
      )}
      {payload.test && (
        <p className="share-test-note" role="note">
          Test organisation. This is not a real document.
        </p>
      )}
      <div className="share-toolbar">
        <button className="button secondary" onClick={print} type="button">
          Print / Save as PDF
        </button>
        <button className="button secondary" onClick={copyLink} type="button">
          Copy link
        </button>
        <span aria-live="polite" className="share-copied" role="status">
          {copied ? 'Link copied' : ''}
        </span>
      </div>

      <div className="share-letterhead">
        <div className="share-business">
          <h2 className="share-business-name">{letterhead.name}</h2>
          <address className="share-business-meta">
            {letterhead.address && <span>{letterhead.address}</span>}
            {letterhead.gstNo && <span>GSTIN {letterhead.gstNo}</span>}
            {letterhead.phone && (
              <span>
                <a href={`tel:${letterhead.phone.replace(/\s+/g, '')}`}>{letterhead.phone}</a>
              </span>
            )}
            {letterhead.email && (
              <span>
                <a href={`mailto:${letterhead.email}`}>{letterhead.email}</a>
              </span>
            )}
          </address>
        </div>

        <div className="share-doc-meta">
          <span className="eyebrow">{documentLabel(payload.kind, payload.audience)}</span>
          <span>Generated {formatDateTime(payload.generatedAt)}</span>
          <span>{expiryNote(meta.expiresAt)}</span>
        </div>
      </div>

      <h1 className="share-title">{payload.title}</h1>

      {payload.recipient?.name && <p className="share-recipient">For {payload.recipient.name}</p>}

      {expired && (
        <p className="share-expired-banner">
          This link has expired. Ask the sender for an up-to-date{' '}
          {isStatus ? 'status' : 'statement'}.
        </p>
      )}

      {payload.kind === 'TRIP_STATUS' && payload.status && <StatusHeader status={payload.status} />}

      {payload.message && <blockquote className="share-message">{payload.message}</blockquote>}

      {payload.summary.length > 0 && (
        <ul className="share-summary">
          {payload.summary.map(tile => (
            <li className="share-tile" data-tone={tile.tone ?? 'neutral'} key={tile.label}>
              <span className="share-tile-label">{tile.label}</span>
              <strong className="share-tile-value">{formatTile(tile)}</strong>
            </li>
          ))}
        </ul>
      )}

      {payload.datasets.map(dataset => (
        <DatasetTable dataset={dataset} expandAll={expandAll} key={dataset.id} />
      ))}

      <footer className="share-doc-footer">
        Shared securely via Transport Book. {expiryNote(meta.expiresAt)}
      </footer>
    </article>
  );
}
